import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  Phone, 
  PhoneOff,
  Users,
  MessageSquare,
  FileText,
  Sparkles,
  Copy,
  Check,
  Loader2,
  ScreenShare,
  ScreenShareOff,
  Settings,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

export default function MeetingRoomPage() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showMinutes, setShowMinutes] = useState(false);
  const [notes, setNotes] = useState('');
  const [generatingMinutes, setGeneratingMinutes] = useState(false);
  const [minutes, setMinutes] = useState(null);
  const [copied, setCopied] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    if (roomCode) {
      fetchMeeting();
    }
  }, [roomCode]);

  useEffect(() => {
    let interval;
    if (isJoined && meeting?.status === 'in_progress') {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isJoined, meeting?.status]);

  const fetchMeeting = async () => {
    try {
      const response = await api.get(`/meetings/room/${roomCode}`);
      setMeeting(response.data);
      if (response.data.minutes) {
        setMinutes(response.data.minutes);
      }
    } catch (error) {
      toast.error('Meeting room not found');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  };

  const startMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return true;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Could not access camera/microphone. Please check permissions.');
      return false;
    }
  };

  const stopMedia = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  };

  const joinMeeting = async () => {
    const mediaStarted = await startMedia();
    if (!mediaStarted) return;
    
    try {
      // Log participant join
      await api.post(`/meetings/${meeting.id}/join`);
      
      // Start meeting if not already started
      if (meeting.status === 'scheduled') {
        await api.post(`/meetings/${meeting.id}/start`);
        setMeeting(prev => ({ ...prev, status: 'in_progress' }));
      }
      
      setIsJoined(true);
      toast.success('Joined meeting');
    } catch (error) {
      toast.error('Failed to join meeting');
    }
  };

  const leaveMeeting = async () => {
    try {
      await api.post(`/meetings/${meeting.id}/leave`);
      stopMedia();
      setIsJoined(false);
      toast.success('Left meeting');
      navigate('/calendar');
    } catch (error) {
      stopMedia();
      navigate('/calendar');
    }
  };

  const endMeeting = async () => {
    if (!window.confirm('End meeting for everyone?')) return;
    
    try {
      await api.post(`/meetings/${meeting.id}/end`);
      stopMedia();
      setIsJoined(false);
      toast.success('Meeting ended');
      navigate('/calendar');
    } catch (error) {
      toast.error('Failed to end meeting');
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const shareScreen = async () => {
    try {
      if (isScreenSharing) {
        // Stop screen sharing
        stopMedia();
        await startMedia();
        setIsScreenSharing(false);
      } else {
        // Start screen sharing
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setIsScreenSharing(true);
        
        // Handle screen share stop
        stream.getVideoTracks()[0].onended = async () => {
          await startMedia();
          setIsScreenSharing(false);
        };
      }
    } catch (error) {
      console.error('Screen share error:', error);
    }
  };

  const generateAIMinutes = async () => {
    if (!notes.trim()) {
      toast.error('Please add meeting notes first');
      return;
    }
    
    setGeneratingMinutes(true);
    try {
      const response = await api.post('/meetings/generate-minutes', {
        meeting_id: meeting.id,
        transcript: notes
      });
      
      setMinutes(response.data.minutes);
      toast.success('Meeting minutes generated!');
      setShowMinutes(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate minutes');
    } finally {
      setGeneratingMinutes(false);
    }
  };

  const copyRoomCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Room code copied');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900 text-white">
        <p>Meeting not found</p>
      </div>
    );
  }

  // Pre-join screen
  if (!isJoined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">{meeting.title}</CardTitle>
            <p className="text-slate-400">{meeting.scheduled_date} • {meeting.start_time} - {meeting.end_time}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Video Preview */}
            <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden relative">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <Badge variant="outline" className="bg-slate-800/80 text-white border-slate-600">
                  {user?.first_name} {user?.last_name}
                </Badge>
              </div>
            </div>
            
            {/* Room Code */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Room Code:</span>
                <code className="bg-slate-700 px-3 py-1 rounded text-white font-mono">{roomCode}</code>
                <Button variant="ghost" size="icon" onClick={copyRoomCode}>
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                </Button>
              </div>
              
              {/* Share Meeting Link */}
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => {
                  const meetingUrl = window.location.href;
                  navigator.clipboard.writeText(meetingUrl);
                  toast.success('Meeting link copied! Share with others to invite them.');
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Meeting Link to Share
              </Button>
              <p className="text-xs text-slate-500 text-center">
                Share this link with others so they can join the meeting
              </p>
            </div>

            {/* Join Button */}
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/calendar')}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={joinMeeting}
                className="bg-[#2a9d8f] hover:bg-[#2a9d8f]/90 text-white px-8"
                data-testid="join-meeting-btn"
              >
                <Video className="w-4 h-4 mr-2" />
                Join Meeting
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // In-meeting view
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Meeting Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-white font-semibold">{meeting.title}</h1>
          <Badge className="bg-red-500 animate-pulse">LIVE</Badge>
          <span className="text-slate-400 flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatTime(elapsedTime)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <code className="bg-slate-700 px-2 py-1 rounded text-white text-sm">{roomCode}</code>
          <Button variant="ghost" size="icon" onClick={copyRoomCode}>
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Video Area */}
        <div className="flex-1 p-4">
          <div className="h-full bg-slate-800 rounded-xl overflow-hidden relative">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4">
              <Badge className="bg-slate-800/80 text-white">
                {user?.first_name} {user?.last_name} (You)
              </Badge>
            </div>
            {!isVideoOn && (
              <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white text-3xl font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Side Panel */}
        {(showNotes || showMinutes) && (
          <div className="w-96 bg-slate-800 border-l border-slate-700 p-4 overflow-y-auto">
            {showNotes && (
              <div className="space-y-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Meeting Notes
                </h3>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Type meeting notes here... Include key discussion points, decisions, and action items."
                  className="min-h-[300px] bg-slate-700 border-slate-600 text-white"
                />
                <Button
                  onClick={generateAIMinutes}
                  disabled={generatingMinutes || !notes.trim()}
                  className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f]"
                >
                  {generatingMinutes ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Minutes
                    </>
                  )}
                </Button>
              </div>
            )}

            {showMinutes && minutes && (
              <div className="space-y-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Meeting Minutes
                </h3>
                
                <div className="bg-slate-700 rounded-lg p-4 space-y-4 text-white">
                  <div>
                    <h4 className="text-sm text-slate-400">Summary</h4>
                    <p className="text-sm">{minutes.summary}</p>
                  </div>
                  
                  {minutes.decisions?.length > 0 && (
                    <div>
                      <h4 className="text-sm text-slate-400">Decisions</h4>
                      <ul className="text-sm list-disc list-inside">
                        {minutes.decisions.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {minutes.action_items?.length > 0 && (
                    <div>
                      <h4 className="text-sm text-slate-400 mb-2">Action Items Table</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-slate-600">
                          <thead className="bg-slate-600">
                            <tr>
                              <th className="text-left p-2 border-r border-slate-500">Sr No</th>
                              <th className="text-left p-2 border-r border-slate-500">Discussed Topics</th>
                              <th className="text-left p-2 border-r border-slate-500">Responsible Person</th>
                              <th className="text-left p-2 border-r border-slate-500">Action Taken</th>
                              <th className="text-left p-2">Yet to Take</th>
                            </tr>
                          </thead>
                          <tbody>
                            {minutes.action_items.map((item, i) => (
                              <tr key={i} className="border-b border-slate-700">
                                <td className="p-2 border-r border-slate-700">{item.sr_no}</td>
                                <td className="p-2 border-r border-slate-700">{item.discussed_topic}</td>
                                <td className="p-2 border-r border-slate-700">{item.responsible_person}</td>
                                <td className="p-2 border-r border-slate-700">
                                  {item.action_taken || <span className="text-slate-500">-</span>}
                                </td>
                                <td className="p-2">
                                  {item.yet_to_take || <span className="text-slate-500">-</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <div className="bg-slate-800 border-t border-slate-700 px-4 py-4">
        <div className="flex items-center justify-center gap-4">
          {/* Audio Toggle */}
          <Button
            variant={isAudioOn ? "outline" : "destructive"}
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={toggleAudio}
            data-testid="toggle-audio-btn"
          >
            {isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </Button>

          {/* Video Toggle */}
          <Button
            variant={isVideoOn ? "outline" : "destructive"}
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={toggleVideo}
            data-testid="toggle-video-btn"
          >
            {isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </Button>

          {/* Screen Share */}
          <Button
            variant={isScreenSharing ? "secondary" : "outline"}
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={shareScreen}
          >
            {isScreenSharing ? <ScreenShareOff className="w-5 h-5" /> : <ScreenShare className="w-5 h-5" />}
          </Button>

          {/* Notes Toggle */}
          <Button
            variant={showNotes ? "secondary" : "outline"}
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={() => {
              setShowNotes(!showNotes);
              setShowMinutes(false);
            }}
          >
            <FileText className="w-5 h-5" />
          </Button>

          {/* Minutes Toggle */}
          {minutes && (
            <Button
              variant={showMinutes ? "secondary" : "outline"}
              size="icon"
              className="w-12 h-12 rounded-full"
              onClick={() => {
                setShowMinutes(!showMinutes);
                setShowNotes(false);
              }}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}

          {/* Leave/End Meeting */}
          <Button
            variant="destructive"
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={meeting.created_by === user?.id ? endMeeting : leaveMeeting}
            data-testid="leave-meeting-btn"
          >
            <PhoneOff className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
